import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { BreadcrumbService } from 'src/app/@core/services/breadcrumb.service';
import { IdentityService } from 'src/app/@core/services/identity.service';
import { LanguageService } from 'src/app/services/language.service';
import { LanguageEnum } from 'src/app/shared/models/item.model';
import { PageType } from '../enums/page-type.enum';
import { PageModel } from '../model/page.model';
import { PagesService } from '../service/pages.service';
@Component({
  selector: 'app-static-page',
  templateUrl: './static-page.component.html',
  styleUrls: ['./static-page.component.scss']
})
export class StaticPageComponent implements OnInit {
  @ViewChild('content', { static: true }) contentElementRef!: ElementRef;
  result: PageModel = {} as PageModel;
  languageId: number = 2;
  isRtl: boolean;
  pageType = PageType;
  /** The request has come back (successfully or not) - used to tell "loading" from "no content". */
  loaded: boolean = false;
  hasContent: boolean = false;
  /** A CMS page id (`page/:id`) that the lookup does not resolve; the fixed-slug pages always exist. */
  notFound: boolean = false;

  constructor(private service: PagesService,
    private router: Router,
    private route: ActivatedRoute,
    private languageService: LanguageService,
    private breadcrumbService: BreadcrumbService,
    private identityService: IdentityService) {
    scrollTo(0, 0);
  }

  get isUnified(): boolean {
    return this.identityService.isUnifiedCode();
  }

  /** Translation key of the fixed-slug page title (same keys as the Custom-mode headings); null for other types. */
  get titleKey(): string | null {
    return StaticPageComponent.titleKeys[this.result.type] ?? null;
  }

  private static readonly titleKeys: Partial<Record<PageType, string>> = {
    [PageType.Policy]: 'footer.privacy',
    [PageType.Terms]: 'footer.generalTerms',
    [PageType.AboutUs]: 'footer.about_us',
    [PageType.UserGuide]: 'footer.userGuide',
    [PageType.TraineeGuide]: 'footer.traineeGuide',
  };

  ngOnInit(): void {
    this.isRtl = this.languageService.getCurrentLanguage() === LanguageEnum.ar;
    this.languageService.languageChange.subscribe((result: LanguageEnum) => {
      this.isRtl = result === LanguageEnum.ar;
    });

    this.router.url.includes('privacy') ? this.result.type = PageType.Policy :
      this.router.url.includes('terms') ? this.result.type = PageType.Terms :
        this.router.url.includes('about') ? this.result.type = PageType.AboutUs :
          this.router.url.includes('user-guide') ? this.result.type = PageType.UserGuide :
            this.router.url.includes('trainee-guide') ? this.result.type = PageType.TraineeGuide : '';

    this.route.params.subscribe((params: Params) => {
      if (params['id']) {
        this.result.id = +params['id'];
        this.result.type = PageType.Custom;
      }

      this.loadPageContent();
    });
  }

  loadPageContent() {
    const pageType = this.result.type;
    this.loaded = false;
    this.notFound = false;
    this.service.getPage(pageType, this.result.id ? this.result.id : null).subscribe({
      next: (result: PageModel) => {
        this.notFound = pageType === PageType.Custom && !result;
        this.result = result ?? ({ type: pageType } as PageModel);
        const body = this.isRtl ? this.result.contentAr : this.result.content;
        this.hasContent = !!body && body.trim().length > 0;
        this.contentElementRef.nativeElement.innerHTML = body ?? '';
        this.updateBreadcrumb(pageType);
      },
      error: () => {
        this.result = { type: pageType } as PageModel;
        this.hasContent = false;
        this.notFound = pageType === PageType.Custom;
        this.updateBreadcrumb(pageType);
        this.loaded = true;
      },
      complete: () => (this.loaded = true),
    });
  }

  /**
   * Unified Code breadcrumb for CMS pages (`page/:id`): the route declares no step, so once the page loads its
   * bilingual title is the only step after Home (the generic "page" label is used only for a page without a
   * title). The fixed-slug pages keep their route label only.
   */
  private updateBreadcrumb(pageType: PageType): void {
    if (pageType !== PageType.Custom) return;
    if (this.notFound) {
      this.breadcrumbService.set([{ labelKey: 'emptyState.pageNotFound' }]);
      return;
    }
    this.breadcrumbService.set([this.result.title || this.result.titleAr
      ? { text: this.result.title, textAr: this.result.titleAr }
      : { labelKey: 'unifiedShell.pages.page' }]);
  }
}
